import type { LocationQuery } from 'vue-router';

import type { EntryCta, EntrySource, SelectedEntityInput } from '~/graphql/generated/graphql';

import { UNIFIED_ROUTE_LOGIN } from '~/constants/routes/unified';
import { buildEntryTrackingQueryString } from '~/utils/unified/entryTracking';
import { buildSelectedEntityQueryString } from '~/utils/unified/selectedEntity';

export const UTM_KEY_QUERY_PARAM = 'utm_key';

/** What the server will store. Anything longer is not a campaign key, it is a mangled URL. */
export const UTM_KEY_MAX_LENGTH = 255;

/**
 * The campaign key as it travels: a query param, from the ad's landing URL through every signup
 * link to /login, where signup reads it back off the route. Empty is the same as absent.
 *
 * A key the server would refuse reads as absent too. Attribution is the least important thing
 * happening on this route — carrying an oversize key forward would fail the signup mutation at the
 * last step and cost the account, so the key is what gets dropped.
 */
export const readUtmKeyFromQuery = (query: LocationQuery): string => {
  const value = query[UTM_KEY_QUERY_PARAM];

  if (typeof value !== 'string' || value.length > UTM_KEY_MAX_LENGTH) {
    return '';
  }

  return value;
};

export interface SignupLinkParams {
  entrySource: EntrySource | string;
  entryCta: EntryCta | string;
  selectedEntity?: SelectedEntityInput | null;
  utmKey?: string | null;
}

/**
 * The one shape of a public signup link. Everything signup needs to know about the arrival —
 * the lane, the CTA, the entity being looked at, the campaign — rides the query string, so a
 * link is identical on the server and the client and survives a copy, a new tab, or no JS.
 */
export const buildSignupLink = ({ entrySource, entryCta, selectedEntity, utmKey }: SignupLinkParams): string => {
  const params = [
    ...(selectedEntity ? [buildSelectedEntityQueryString(selectedEntity)] : []),
    buildEntryTrackingQueryString(entrySource, entryCta),
    ...(utmKey ? [`${UTM_KEY_QUERY_PARAM}=${encodeURIComponent(utmKey)}`] : []),
  ];

  return `${UNIFIED_ROUTE_LOGIN}?${params.join('&')}`;
};
